import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
  Drawer,
  Badge,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Contacts as ContactsIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  AddComment as AddCommentIcon,
  BarChart as BarChartIcon,
  Sync as SyncIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { countries } from '../data/countries';
import MessageComposer from './MessageComposer';
import Inbox from './Inbox';
import ConversationDetail from './ConversationDetail';
import Contacts from './Contacts';
import Groups from './Groups';
import GroupDetail from './GroupDetail';
import Scheduler from './Scheduler';
import Templates from './Templates';
import ClientUsageDashboard from './ClientUsageDashboard';
import ExecutionHistory from './ExecutionHistory';
import axios from 'axios';
import { Socket } from 'socket.io-client';

type ViewMode = 'messages' | 'inbox' | 'contacts' | 'groups' | 'scheduler' | 'templates' | 'usage';

const DRAWER_WIDTH = 220;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [socket] = useState<Socket | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const [viewMode, setViewMode] = useState<ViewMode>('inbox');
  const [selectedConversation, setSelectedConversation] = useState<{ phoneNumber: string; contactName?: string } | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<{ id: string; name: string } | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedScheduledMessageId, setSelectedScheduledMessageId] = useState<string>('');
  const [phoneNumberToAdd, setPhoneNumberToAdd] = useState<string>('');

  // Fetch unread count for inbox badge
  const fetchUnreadCount = useCallback(async () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const num = u.twilioNumber;
      const url = num ? `/api/conversations?twilioNumber=${encodeURIComponent(num)}` : '/api/conversations';
      const response = await axios.get(url);
      const conversations = response.data.conversations || [];
      const total = conversations.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
      setUnreadCount(total);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await axios.get('/api/messages/sync');
      setLastSyncTime(new Date());
      setSnackbar({ open: true, message: 'Messages synced successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error syncing messages:', error);
      setSnackbar({ open: true, message: 'Failed to sync messages.', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleViewChange = (view: ViewMode) => {
    setViewMode(view);
    setSelectedConversation(null);
    setSelectedGroup(null);
    setMobileDrawerOpen(false);
  };

  const handleConversationSelect = (phoneNumber: string, contactName?: string) => {
    setSelectedConversation({ phoneNumber, contactName });
  };

  const handleGroupSelect = (groupId: string, groupName: string) => {
    setSelectedGroup({ id: groupId, name: groupName });
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
  };

  const handleViewHistory = (messageId: string) => {
    setSelectedScheduledMessageId(messageId);
    setHistoryDialogOpen(true);
  };

  const handleAddContact = (phoneNumber: string) => {
    setPhoneNumberToAdd(phoneNumber);
    setViewMode('contacts');
  };

  const navItems = [
    { id: 'inbox' as ViewMode, label: 'Inbox', icon: <Badge badgeContent={unreadCount} color="error" max={99}><InboxIcon /></Badge> },
    { id: 'messages' as ViewMode, label: 'Send Message', icon: <SendIcon /> },
    { id: 'contacts' as ViewMode, label: 'Contacts', icon: <ContactsIcon /> },
    { id: 'groups' as ViewMode, label: 'Groups', icon: <GroupIcon /> },
    { id: 'scheduler' as ViewMode, label: 'Scheduled', icon: <ScheduleIcon /> },
    { id: 'templates' as ViewMode, label: 'Templates', icon: <DescriptionIcon /> },
    { id: 'usage' as ViewMode, label: 'Usage', icon: <BarChartIcon /> },
  ];

  const renderContent = () => {
    if (viewMode === 'messages') {
      return (
        <MessageComposer
          twilioNumber={user?.twilioNumber || undefined}
        />
      );
    }

    if (viewMode === 'inbox') {
      return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 'calc(100vh - 100px)' }, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          {/* Left: Conversation List - always visible on desktop, hidden on mobile when conversation selected */}
          <Box sx={{ width: { xs: '100%', md: 340 }, borderRight: { xs: 'none', md: '1px solid #e8e8ef' }, flexShrink: 0, display: { xs: selectedConversation ? 'none' : 'flex', md: 'flex' }, flexDirection: 'column', height: { xs: 'calc(100vh - 140px)', md: 'auto' } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3436' }}>Inbox</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lastSyncTime && (
                  <Typography variant="caption" sx={{ color: '#999', fontSize: 10 }}>
                    Synced {lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                )}
                <Tooltip title="Sync messages">
                  <IconButton onClick={handleSync} disabled={syncing} size="small" sx={{ color: '#6c5ce7' }}>
                    {syncing ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Inbox twilioNumber={user?.twilioNumber || ''} socket={socket} onConversationSelect={handleConversationSelect} onAddContact={handleAddContact} selectedPhone={selectedConversation?.phoneNumber || null} />
          </Box>
          {/* Right: Conversation Detail */}
          <Box sx={{ flex: 1, display: { xs: selectedConversation ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', height: { xs: 'calc(100vh - 140px)', md: 'auto' } }}>
            {selectedConversation ? (
              <>
                <Button onClick={() => setSelectedConversation(null)} sx={{ alignSelf: 'flex-start', m: 1, textTransform: 'none', color: '#6c5ce7', display: { xs: 'block', md: 'none' } }}>← Back</Button>
                <ConversationDetail phoneNumber={selectedConversation.phoneNumber} contactName={selectedConversation.contactName} twilioNumber={user?.twilioNumber || ''} socket={socket} />
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ color: '#999' }}>Select a conversation to view messages</Typography>
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    if (viewMode === 'contacts') {
      return <Contacts phoneNumberToAdd={phoneNumberToAdd} onContactAdded={() => setPhoneNumberToAdd('')} />;
    }

    if (viewMode === 'groups') {
      if (selectedGroup) {
        return <GroupDetail groupId={selectedGroup.id} groupName={selectedGroup.name} onBack={handleBackToGroups} />;
      }
      return <Groups onGroupSelect={handleGroupSelect} />;
    }

    if (viewMode === 'scheduler') {
      return <Scheduler twilioNumber={user?.twilioNumber || ''} onViewHistory={handleViewHistory} />;
    }

    if (viewMode === 'templates') {
      return <Templates />;
    }

    if (viewMode === 'usage') {
      return <ClientUsageDashboard />;
    }

    return null;
  };

  const userDisplayName = user?.twilioNumber || user?.loginId || 'User';
  const userCountryCode = user?.countryCode || '1';
  const country = countries.find(c => c.callingCode === userCountryCode);

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#6c5ce7', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>T</Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#2d3436' }}>ConnectPie SMS</Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>SMS Platform</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Button fullWidth variant="contained" startIcon={<AddCommentIcon />} onClick={() => handleViewChange('messages')} sx={{ bgcolor: '#6c5ce7', borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { bgcolor: '#5a4bd1' } }}>New Message</Button>
      </Box>
      <List sx={{ px: 1, flex: 1, mt: 1 }}>
        {navItems.map((item) => (
          <ListItemButton key={item.id} selected={viewMode === item.id} onClick={() => handleViewChange(item.id)} sx={{ borderRadius: 2, mb: 0.3, py: 1, '&.Mui-selected': { bgcolor: '#f0edff', color: '#6c5ce7', '& .MuiListItemIcon-root': { color: '#6c5ce7' } }, '&:hover': { bgcolor: '#f8f7ff' } }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#999' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: viewMode === item.id ? 600 : 400 }} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ p: 2, borderTop: '1px solid #e8e8ef' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#ddd', fontSize: 13 }}>{userDisplayName.charAt(0).toUpperCase()}</Avatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userDisplayName}</Typography>
            <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>{country ? `${country.code} +${country.callingCode}` : `+${userCountryCode}`}</Typography>
          </Box>
          <IconButton size="small" onClick={logout} sx={{ color: '#475569' }}><LogoutIcon fontSize="small" /></IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f9' }}>
      {/* Mobile Drawer */}
      <Drawer open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
        {sidebarContent}
      </Drawer>
      {/* Desktop Sidebar */}
      <Box data-sidebar="desktop" sx={{ width: DRAWER_WIDTH, flexShrink: 0, bgcolor: '#fff', borderRight: '1px solid #e8e8ef', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1200 }}>
        {sidebarContent}
      </Box>
      {/* Main Content */}
      <Box data-main-content sx={{ flex: 1, ml: { xs: 0, md: `${DRAWER_WIDTH}px` }, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, maxWidth: '100%' }}>
        {/* Top Bar */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            px: 2,
            py: 1.5,
            bgcolor: '#fff',
            borderBottom: '1px solid #e8e8ef',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
          }}
        >
          <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: '#555' }}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Page Content */}
        <Box sx={{ p: { xs: 1.5, md: 3 }, flex: 1, overflowX: 'hidden' }}>
          {renderContent()}
        </Box>
      </Box>

      {/* Execution History Dialog */}
      <ExecutionHistory
        messageId={selectedScheduledMessageId}
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
